import { apiService as api } from "@/store/apiService";
import type { AppView, ModuleTemplate } from "./workspaceModel";

export type WorkspaceOptions = {
  connections: { id: string; name: string }[];
  workflows: { id: string; name: string }[];
  groups: { id: string; name: string }[];
  assignments: { applicationId: string; groupId: string }[];
  canConfigureSources: boolean;
};
export type ApplicationAccessGroup = {
  id: string;
  name: string;
  description: string;
  memberCount: number;
  hasApplicationCategory: boolean;
  isCurrentUserMember: boolean;
  assigned: boolean;
};
export type ApplicationAccessState = {
  applicationId: string | null;
  revision: string;
  canManageGroups: boolean;
  isSuperAdmin: boolean;
  hasApplicationCategory: boolean;
  groups: ApplicationAccessGroup[];
};
export type RuntimeView = {
  id: string;
  title: string;
  kind: AppView["kind"];
  description: string;
  layout?: "grid" | "list";
  moduleTitle: string;
  relatedViews?: { id: string; title: string; kind: AppView["kind"]; description: string }[];
  cards?: { id: string; title: string; description: string; targetViewId?: string | null }[];
  configured: boolean;
  workflowId?: string | null;
};
export type RuntimeApp = {
  id: string;
  name: string;
  views: RuntimeView[];
  defaultViewId?: string | null;
  hasArchive: boolean;
  canManage: boolean;
  canUseWorkflows: boolean;
};
export type DataPage = {
  columns: string[];
  rows: Record<string, unknown>[];
  page: number;
  hasMore: boolean;
  refreshedAt: string;
};
export type SourceColumn = {
  schema: string;
  name: string;
  column: string;
  kind: string;
};
const workspaceApi = api
  .enhanceEndpoints({
    addTagTypes: [
      "workspace_templates",
      "workspace_runtime",
      "workspace_options",
      "settings_groups",
      "settings_user_applications",
      "settings_user_permissions",
      "director_agents",
      "director_agent",
      "external_connections",
    ],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      getApplicationAccess: build.query<ApplicationAccessState, string | undefined>({
        query: (id) => id
          ? `/api/application-workspaces/apps/${id}/access`
          : "/api/application-workspaces/access-options",
        providesTags: ["workspace_options"],
      }),
      updateApplicationAccess: build.mutation<ApplicationAccessState, {
        id: string;
        expectedRevision: string;
        addGroupIds: string[];
        removeGroupIds: string[];
      }>({
        query: ({ id, ...body }) => ({
          url: `/api/application-workspaces/apps/${id}/access`,
          method: "PATCH",
          body,
        }),
        invalidatesTags: ["workspace_options", "workspace_runtime", "settings_groups", "settings_user_applications", "settings_user_permissions", "director_agents", "director_agent"],
      }),
      getModuleTemplates: build.query<ModuleTemplate[], void>({
        query: () => "/api/application-workspaces/templates",
        providesTags: ["workspace_templates"],
      }),
      saveModuleTemplate: build.mutation<
        ModuleTemplate,
        {
          id: string;
          name: string;
          description: string;
          version: number;
          views: AppView[];
          requiredFields?: string[];
          fieldTypes?: Record<string, "text" | "number" | "currency" | "date" | "boolean" | "list">;
        }
      >({
        query: (body) => ({
          url: `/api/application-workspaces/templates/${body.id}`,
          method: "PUT",
          body,
        }),
        invalidatesTags: ["workspace_templates"],
      }),
      deleteModuleTemplate: build.mutation<void, string>({
        query: (id) => ({
          url: `/api/application-workspaces/templates/${id}`,
          method: "DELETE",
        }),
        invalidatesTags: ["workspace_templates"],
      }),
      getWorkspaceOptions: build.query<WorkspaceOptions, void>({
        query: () => "/api/application-workspaces/options",
        providesTags: ["external_connections", "workspace_options"],
      }),
      getSourceSchema: build.query<SourceColumn[], string>({
        query: (id) => `/api/application-workspaces/sources/${id}/schema`,
        providesTags: ["external_connections"],
      }),
      getRuntimeApp: build.query<RuntimeApp, string>({
        query: (id) => `/api/application-workspaces/apps/${id}`,
        providesTags: ["workspace_runtime"],
        keepUnusedDataFor: 0,
      }),
      getAppData: build.query<
        DataPage,
        {
          id: string;
          viewId: string;
          page: number;
          search: string;
          sort?: string;
          descending?: boolean;
        }
      >({
        query: ({ id, ...params }) => ({
          url: `/api/application-workspaces/apps/${id}/data`,
          params,
        }),
        keepUnusedDataFor: 0,
        providesTags: ["external_connections"],
      }),
    }),
  });
export const {
  useGetApplicationAccessQuery,
  useLazyGetApplicationAccessQuery,
  useUpdateApplicationAccessMutation,
  useGetModuleTemplatesQuery,
  useSaveModuleTemplateMutation,
  useDeleteModuleTemplateMutation,
  useGetWorkspaceOptionsQuery,
  useGetSourceSchemaQuery,
  useGetRuntimeAppQuery,
  useGetAppDataQuery,
} = workspaceApi;
export function workspaceError(error: unknown): string {
  const value = error as {
    data?: { message?: string; errors?: Record<string, string[]> };
    status?: number;
  };
  if (value?.data?.message) return value.data.message;
  if (value?.data?.errors)
    return Object.values(value.data.errors).flat().join(" ");
  if (value?.status === 403)
    return "You don’t have access to this application or action.";
  if (value?.status === 404) return "This item no longer exists.";
  return "Something went wrong. Please try again.";
}
